import { eagerLazy } from "@/lib/eagerLazy";
import { preloadHomeChunks } from "@/lib/prerenderedHomeChunks";
import { preloadAppChunks } from "@/lib/prerenderedAppChunks";

/**
 * Marketing route components that ship as prerendered HTML.
 * Using `eagerLazy` means that if `main.tsx` preloads the matching module
 * before calling `hydrateRoot`, React renders them synchronously and
 * adopts the prerendered DOM with no Suspense fallback flash.
 *
 * Components not listed here still use `React.lazy` from routes.tsx — they
 * are not prerendered, so a brief Suspense fallback is acceptable.
 */

export const PublicShellLazy = eagerLazy(() =>
  import("@/components/layout/PublicShell").then((m) => ({ default: m.PublicShell }))
);

export const HomeLazy = eagerLazy(() => import("@/pages/Home"));

export const BlogIndexLazy = eagerLazy(() => import("@/pages/blog/BlogIndexPage"));

// Public blog post page. Snapshot-prerendered for every published slug, so
// the chunk must be preloaded before hydration to avoid a Suspense wipe that
// triggers a duplicate <picture> request, a refetch of the post payload,
// and an extra paint cycle. Adds ~1 chunk to the critical path on hard
// loads of `/blog/<slug>/` and removes 2–3 follow-up network requests.
export const BlogPostLazy = eagerLazy(() => import("@/pages/blog/BlogPostPage"));

/**
 * Map of pathname → component preload function. Only includes routes that
 * use `eagerLazy` above. `main.tsx` consults this on boot so the matched
 * route module is awaited before hydration.
 */
export const PRERENDERED_ROUTE_PRELOADERS: Record<string, () => Promise<void>> = {
  "/": async () => {
    // For the homepage we additionally preload every nested lazy chunk
    // that the prerender baked into the static HTML. Without this, those
    // inner Suspense boundaries render `null` on hydration and React
    // throws #418 / #423 then bails out of hydration entirely.
    await Promise.all([HomeLazy.preload(), preloadHomeChunks()]);
  },
  "/blog": async () => {
    // Preload the Blog index chunk so the prerendered card grid + the
    // inlined `__blog_index_page__` script are still in the DOM when
    // BlogIndexPage's lazy state initializer runs. Without this preload,
    // React's first render falls through Suspense, wipes the prerendered
    // root, and the component remounts with `posts = null` → triggers a
    // second fetch and a duplicate batch of cover image requests.
    await BlogIndexLazy.preload();
  },
};

/** Preload the public shell + the component for the given pathname (if known). */
export async function preloadPrerenderedRoute(pathname: string): Promise<void> {
  const normalised = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  let routePreload = PRERENDERED_ROUTE_PRELOADERS[normalised];
  // `/blog/<slug>` is dynamic — match any single-segment slug under
  // `/blog/` (but not `/blog` itself) and preload the BlogPost chunk so
  // the snapshot does not Suspense-wipe on hard refresh.
  if (!routePreload && /^\/blog\/page\/[^/]+$/.test(normalised)) {
    // Path-based archive pages share the BlogIndex chunk — preload it so
    // the per-page snapshot adopts cleanly with no Suspense wipe.
    routePreload = async () => {
      await BlogIndexLazy.preload();
    };
  } else if (!routePreload && /^\/blog\/[^/]+$/.test(normalised)) {
    routePreload = async () => {
      await BlogPostLazy.preload();
    };
  }
  // Always preload app-level chrome (toaster, sonner, branding injector)
  // that the prerender bakes into the static HTML on every route — they
  // sit in App.tsx's top-level <Suspense> and would otherwise render
  // `null` on first hydration and trigger React #418 / #423.
  const work: Promise<void>[] = [PublicShellLazy.preload(), preloadAppChunks()];
  if (routePreload) work.push(routePreload());
  await Promise.all(work);
}